import React, { useState, useRef, useEffect } from "react";
import { navigate } from "@reach/router";
import { useDocument } from "./loan_tool";
import { PURCHASE } from "../../constants/document_types";

export default function FormsMenu({ documentId }) {
  const [isOpen, setIsOpen] = useState(false);
  // When set (to a letter key), the "select your loan first" dialog is shown.
  const [dialogLetter, setDialogLetter] = useState(null);
  const containerRef = useRef(null);
  const [loanDocument, , { documentType }] = useDocument();

  const FORMS = [
    {
      label: "Borrower Authorization",
      path: documentId ? `/loan-tool/${documentId}/forms/borrower-auth` : null,
      newTab: true
    },
    {
      label: "Borrower Authorization and Certification",
      path: documentId ? `/loan-tool/${documentId}/forms/borrower-auth-cert` : null,
      newTab: true
    },
    {
      label: "Credit Authorization",
      path: documentId ? `/loan-tool/${documentId}/forms/credit-auth` : null,
      newTab: true
    },
    {
      label: "DSCR Calculator",
      // In-place (not a new tab): it's a data-entry form that saves the rent roll
      // to the loan file. A new tab loads a separate copy and the original would
      // overwrite it on save.
      path: documentId ? `/loan-tool/${documentId}/forms/dscr-calculator` : null,
      newTab: false
    },
    {
      label: "Fix and Flip Estimate",
      path: documentId ? `/loan-tool/${documentId}/fix-and-flip/page-1` : null,
      newTab: false
    },
    {
      label: "Full Realtor List",
      path: documentId ? `/loan-tool/${documentId}/forms/full-realtor-list` : null,
      newTab: true,
      purchaseOnly: true
    },
    {
      label: "Open House Flyer",
      path: documentId ? `/loan-tool/${documentId}/forms/open-house-flyer` : null,
      newTab: true,
      purchaseOnly: true
    },
    {
      label: "Realtor Agent Information",
      path: documentId ? `/loan-tool/${documentId}/forms/realtor-agent-information` : null,
      // In-place (not a new tab): this is a data-entry form. A new browser tab
      // loads its own copy of the loan, and the original tab then overwrites
      // the saved realtor info on its next save. In-place shares one document,
      // so the save sticks.
      newTab: false,
      purchaseOnly: true
    },
    {
      label: "Pre-Approval Letter",
      path: documentId ? `/loan-tool/${documentId}/forms/pre-approval-letter` : null,
      newTab: true,
      letterKey: "pre-approval-letter"
    },
    {
      label: "Pre-Qualification Letter",
      path: documentId ? `/loan-tool/${documentId}/forms/pre-qual-letter` : null,
      newTab: true,
      letterKey: "pre-qual-letter"
    }
  ].filter(
    form => (!form.letterKey && !form.purchaseOnly) || documentType === PURCHASE
  );

  useEffect(() => {
    function handleClickOutside(e) {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  function handleSelect(form) {
    setIsOpen(false);
    if (!form.path) {
      window.alert(`${form.label} is coming soon.`);
      return;
    }
    // Purchase only: the Pre-Qual / Pre-Approval letters pull details from the
    // loan the borrower selects on the Summary Sheet. Remind the user to make
    // that selection first via a dialog. (Refinance opens the letter directly.)
    if (form.letterKey && documentType === PURCHASE) {
      const loanSelected =
        loanDocument &&
        loanDocument.summary &&
        loanDocument.summary.selection_loan_number;
      if (!loanSelected) {
        setDialogLetter(form.letterKey);
        return;
      }
      // A loan is already selected on the Summary Sheet — open the letter
      // directly instead of re-prompting (this was the endless-loop cause).
    }
    // Open every form in the SAME tab (in-app navigation), like the Realtor Agent
    // button. Opening a form in a NEW browser tab created a separate document
    // "work service": edits made on a nav page in one tab did not reflect in the
    // other, which confused LOs and risked lost data. Staying in one tab keeps a
    // single shared document context. Each form has a "Back" button to return.
    navigate(form.path);
  }

  function goToLoanSelection() {
    const letter = dialogLetter;
    setDialogLetter(null);
    navigate(`/loan-tool/${documentId}/summary-sheet?openLetter=${letter}`);
  }

  return (
    <>
      <div className="forms-menu" ref={containerRef}>
        <button className="button" onClick={() => setIsOpen(!isOpen)}>
          Forms
        </button>
        {isOpen && (
          <ul className="forms-menu-dropdown">
            {FORMS.map(form => (
              <li key={form.label}>
                <button
                  className="forms-menu-item"
                  onClick={() => handleSelect(form)}
                >
                  {form.label}
                  {!form.path && (
                    <span className="forms-menu-item-badge">Coming soon</span>
                  )}
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Loan-selection reminder dialog (Purchase letters only) */}
      {dialogLetter && (
        <div style={{
          position: "fixed", top: 0, left: 0, width: "100%", height: "100%",
          backgroundColor: "rgba(0,0,0,0.5)", zIndex: 9999,
          display: "flex", alignItems: "center", justifyContent: "center"
        }}>
          <div style={{
            backgroundColor: "#fff", borderRadius: "6px", padding: "30px",
            maxWidth: "480px", width: "90%", boxShadow: "0 4px 20px rgba(0,0,0,0.3)"
          }}>
            <p style={{ marginBottom: "24px", lineHeight: "1.5" }}>
              Before you print the Pre-Qualification and Pre-Approved letters you
              need to select the loan and the details you wish to include in the
              letters. Click the button below and TIER|ONE will go to the Summary
              Sheet, where you can select the loan the borrower wants to purchase
              their new home.
            </p>
            <div style={{ display: "flex", gap: "10px", justifyContent: "flex-end" }}>
              <button className="button" onClick={goToLoanSelection}>
                Take me to Loan Selection
              </button>
              <button className="button" onClick={() => setDialogLetter(null)}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
